import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { languageOptions } from "@/i18n/translations";
import api from "@/lib/apiClient";
import { ShoppingCart, User, Heart, MagnifyingGlass as Search, X, List } from "@phosphor-icons/react";

export default function Header() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState([]);

  // Main-menu categories come straight from the shared Categories tree
  // (Admin → Categories). Any active category with sort_order set is rendered
  // in the header automatically — no code change needed to add a new bucket.
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
  }, []);

  // Fixed non-shop entries always follow the dynamic category list. Rentals /
  // Services / About / Blog / Contact are not category pages so they stay
  // hard-coded.
  const staticLinks = [
    { to: "/rental", label: t.nav.rental },
    { to: "/service", label: t.nav.service },
    { to: "/brands", label: t.nav.brands },
    { to: "/about", label: "Story" },
    { to: "/blog", label: t.nav.blog },
    { to: "/contact", label: t.nav.contact },
  ];

  const dynamicShopLinks = categories.map((c) => ({
    to: `/shop/${c.key}`,
    label: c.label,
    subcategories: c.subcategories || [],
  }));

  const navLinks = [...dynamicShopLinks, ...staticLinks];

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/shop?search=${encodeURIComponent(q.trim())}`);
      setShowSearch(false);
      setQ("");
    }
  };

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      {/* Top strip */}
      <div className="bg-black text-white text-[11px] overline py-2 px-6 flex justify-between items-center">
        <span data-testid="header-tagline">Free shipping over €200 in North Macedonia · Imported from Germany</span>
        <div className="flex items-center gap-4">
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="header-admin-link" className="overline text-[color:var(--vt-orange)] hover:text-white">
              ADMIN →
            </Link>
          )}
          <select
            data-testid="lang-switcher"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="bg-transparent border-none text-white outline-none cursor-pointer overline"
          >
            {languageOptions.map((o) => (
              <option key={o.code} value={o.code} className="bg-black text-white">{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main bar */}
      <div className="px-6 lg:px-12 py-5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 no-underline-link">
          <img src="https://customer-assets.emergentagent.com/job_ecommerce-veloteka/artifacts/3afh4xkv_logo%20veloteka.png" alt="VELOTEKA" className="h-11 w-11 object-contain" />
          <span className="font-display font-black text-2xl tracking-tighter hidden sm:inline">VELOTEKA</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((l) => (
            <div key={l.to} className="relative group">
              <NavLink
                to={l.to}
                data-testid={`nav-${l.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold uppercase tracking-wider link-underline ${isActive ? "text-[color:var(--vt-orange)]" : "text-neutral-900"}`
                }
              >
                {l.label}
              </NavLink>
              {l.subcategories?.length > 0 && (
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-40" data-testid={`nav-submenu-${l.to.replace(/\//g,'-')}`}>
                  <div className="bg-white border border-neutral-200 shadow-xl py-2 min-w-[180px]">
                    {l.subcategories.map((s) => (
                      <NavLink
                        key={s}
                        to={`${l.to}?subcategory=${encodeURIComponent(s)}`}
                        className="block px-4 py-2 text-[12px] uppercase tracking-wider text-neutral-800 hover:bg-neutral-50 hover:text-[color:var(--vt-orange)]"
                      >
                        {s}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button data-testid="search-toggle" onClick={() => setShowSearch((s) => !s)} className="hover:text-[color:var(--vt-orange)] transition-colors">
            <Search size={22} weight="regular" />
          </button>
          <Link to="/wishlist" data-testid="wishlist-link" className="hover:text-[color:var(--vt-orange)] transition-colors">
            <Heart size={22} weight="regular" />
          </Link>
          <Link to={user ? "/account" : "/login"} data-testid="account-link" className="hover:text-[color:var(--vt-orange)] transition-colors">
            <User size={22} weight="regular" />
          </Link>
          <Link to="/cart" data-testid="cart-link" className="relative hover:text-[color:var(--vt-orange)] transition-colors">
            <ShoppingCart size={22} weight="regular" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-[color:var(--vt-orange)] text-white text-[10px] font-black w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((s) => !s)} className="lg:hidden">
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {showSearch && (
        <form onSubmit={doSearch} className="border-t border-neutral-200 px-6 lg:px-12 py-4 flex gap-3" data-testid="search-form">
          <input
            className="vt-input"
            autoFocus
            placeholder="Search bikes, parts, accessories..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
          />
          <button type="submit" className="vt-btn-primary" data-testid="search-submit">Search</button>
        </form>
      )}

      {mobileOpen && (
        <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
          <div className="flex flex-col p-6 gap-4">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
                className="text-[13px] font-semibold uppercase tracking-wider">
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <button onClick={() => { logout(); setMobileOpen(false); }} className="vt-btn-outline w-fit" data-testid="mobile-logout">
                {t.nav.logout}
              </button>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="vt-btn-primary w-fit">
                {t.nav.login}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
